/**
 * VoiceConversation Constants
 * Configuration values and prompts for the voice conversation system
 * 
 * Restored from feat/symptom-checker branch with adaptations for unified voice services
 */

// =============================================================================
// TIMING CONFIGURATION
// =============================================================================

// Pause duration before system asks a question (silence after user speaks)
// 850ms: Give user time to finish a phrase without cutting them off (reduces "I had to repeat myself")
export const DEFAULT_PAUSE_DURATION_MS = 850;

// First question after chief complaint - same as default so we don't cut off the answer
export const FIRST_QUESTION_PAUSE_MS = 850;

// Extended pause duration for incomplete sentences
export const EXTENDED_PAUSE_MS = 1800;

// Re-prompt timeout - if user hasn't responded after this many ms, prompt them
export const REPROMPT_TIMEOUT_MS = 10000;

// Maximum age of pre-generated questions (ms) before they're considered stale
export const MAX_QUESTIONS_AGE_MS = 20000;

// Safety limit - conversation ends naturally when LLM has no more questions
// This is just a failsafe to prevent infinite loops
export const MAX_QUESTIONS = 12;

// Minimum speech before considering a pause (prevents triggering on initial silence)
export const MIN_SPEECH_BEFORE_PAUSE_MS = 500;

// Echo cooldown duration - time to wait after TTS playback ends before accepting speech input
// 300ms: Short so user can reply immediately without their first words being dropped
export const ECHO_COOLDOWN_MS = 300;

// After assistant TTS (Web Speech path), wait before turning the mic back on so speaker output
// is not clipped by the browser's echo cancellation / AEC fighting simultaneous capture + playback.
export const POST_TTS_WEB_SPEECH_RESUME_MS = 450;

// Response delay - brief pause after user finishes speaking before system responds
// Creates a more natural, conversational rhythm (not too eager to jump in)
export const RESPONSE_DELAY_MS = 700;

// =============================================================================
// PROMPTS
// =============================================================================

// Message to say when re-prompting after no user response
export const STILL_LISTENING_PROMPT = "I'm still here when you're ready. Take your time.";

// Opening prompt when conversation starts
// Keep this SHORT for fast TTS response - longer explanations slow down the greeting
export const OPENING_PROMPT = "Hi! What seems to be the problem today?";

/** Spoken once before the opening line; not repeated during the session. */
export const MEDICOLEGAL_DISCLAIMER =
  'This tool provides general health information only. It does not diagnose, treat, or triage emergencies. It is not a substitute for care from a licensed clinician. For emergencies, use local emergency services.';

/** After Symptom Discovery when proceeding straight to preliminary analysis (no Symptom Details stage). */
export const STAGE_GATE_AFTER_DISCOVERY_PRELIM_ANALYSIS =
  "We've finished Symptom Discovery, and we can give you a preliminary analysis. Would you like to continue the conversation to better sort this out, or stop here?";

/** Re-prompt when continue/stop gate intent is unclear. */
export const STAGE_GATE_REPROMPT_CONTINUE_OR_STOP =
  "Would you like to continue the conversation to better sort this out, or stop here? Please say continue or stop.";

// Closing prompt when conversation is complete
export const CLOSING_PROMPT = "Thank you for providing all that information. I now have a good understanding of your symptoms. Please review the computational analysis on the right.";

// Closing prompt when max questions reached
export const CLOSING_PROMPT_MAX_REACHED = "Thank you for providing all that information. I now have a good understanding of your symptoms. Please review the analysis, but remember to consult a healthcare provider for proper diagnosis and treatment.";

// =============================================================================
// REFINEMENT TRANSITION - User-controlled transition to diagnosis-specific questions
// =============================================================================

// Question to ask user if they want to continue with diagnosis refinement questions
// Asked after prerequisites are complete and initial DDX is available
export const REFINEMENT_TRANSITION_QUESTION = "Based on what you've told me, I have some ideas about possible conditions. Would you like to answer a few more questions to help narrow things down?";

// =============================================================================
// VOICE CONFIGURATION
// =============================================================================

export const VOICE_CONFIG = {
  voice: 'shimmer',   // Default voice - soft, clear female voice
  speed: 0.95,        // Slightly slower for clarity
};

// =============================================================================
// SUPPORTED LANGUAGES
// =============================================================================

// Language constants and helpers — canonical definitions from shared-services.
// Re-exported so existing consumers of this module (voice-conversation/index.js etc.) keep working.
export {
  SUPPORTED_LANGUAGES,
  getLanguageByCode,
  requiresGoogleSTT,
  shouldUseFlux,
  getDeepgramModel,
  getDeepgramLanguageCode,
} from '@healthdesk/shared-services';

// =============================================================================
// PREREQUISITE QUESTIONS - Asked before LLM clarifying questions
// =============================================================================

/**
 * These questions gather essential information before the LLM takes over:
 * 1. Age and sex (combined demographic question)
 * 2. Duration of chief complaint (dynamically uses actual symptom)
 * 3. Associated symptoms (encourage patient to share)
 * 4. Duration of associated symptoms (conditional)
 * 
 * This comprehensive baseline ensures the LLM has context for intelligent follow-up.
 * 
 * NOTE: Some questions are templates with {chiefComplaint} placeholder
 * that gets replaced dynamically with the patient's actual symptom.
 */
export const PREREQUISITE_QUESTIONS = [
  // Combined demographics
  "First, could you tell me your age and whether you're male or female?",
  // Duration of chief complaint - {chiefComplaint} is replaced dynamically
  "How long have you been experiencing the {chiefComplaint}?",
  // Associated symptoms - encourage open sharing
  "Are there any other symptoms you're experiencing along with this? Please share anything else you've noticed, even if it seems unrelated.",
  // Duration of associated symptoms (asked after they share)
  "And how long have you had these other symptoms?",
];

// Question indices for conditional logic
export const PREREQUISITE_INDICES = {
  DEMOGRAPHICS: 0,           // Combined age + sex
  DURATION_CHIEF: 1,         // Duration of chief complaint (dynamic)
  ASSOCIATED_SYMPTOMS: 2,    // Open-ended associated symptoms
  DURATION_ASSOCIATED: 3,    // Duration of associated (conditional)
};

// Clarification question when chief complaint is unclear
export const CHIEF_COMPLAINT_CLARIFICATION = "I want to make sure I understand - what would you say is your main concern or the symptom that's bothering you the most?";

// Legacy alias for backward compatibility
export const DEMOGRAPHIC_QUESTIONS = [PREREQUISITE_QUESTIONS[0]];

// =============================================================================
// PROXY MODE - For parents/guardians describing symptoms for minors
// =============================================================================

// Age restriction message - shown when a minor tries to use system directly
export const MINOR_USER_MESSAGE = "I'm sorry, but this symptom checker is designed for adults 18 and older. If you're under 18, please have a parent or guardian use the system on your behalf. They can describe your symptoms and get guidance for you.";

// Proxy confirmation question (when chief complaint mentions child/baby)
export const PROXY_CONFIRMATION_QUESTION = "I understand you're concerned about someone else. Are you the parent or guardian of the person you're describing symptoms for?";

// Proxy mode question (when user reports age < 18)
export const MINOR_DETECTED_QUESTION = "I noticed you mentioned being under 18. Are you describing your own symptoms, or are you a parent or guardian speaking for someone else?";

// Proxy mode demographic question
export const PROXY_DEMOGRAPHIC_QUESTION = "Could you tell me the patient's age and whether they're male or female?";

// Proxy mode question variants (use "the patient" / "they" instead of "you")
export const PROXY_PREREQUISITE_QUESTIONS = [
  "Could you tell me the patient's age and whether they're male or female?",
  "How long has the patient been experiencing the {chiefComplaint}?",
  "Are there any other symptoms the patient is experiencing? Please share anything you've noticed, even if it seems unrelated.",
  "And how long have they had these other symptoms?",
];

// Question indices for proxy mode conditional logic
export const PROXY_PREREQUISITE_INDICES = {
  DEMOGRAPHICS: 0,           // Patient age + sex
  DURATION_CHIEF: 1,         // Duration of chief complaint (dynamic)
  ASSOCIATED_SYMPTOMS: 2,    // Open-ended associated symptoms
  DURATION_ASSOCIATED: 3,    // Duration of associated (conditional)
};

// Patterns indicating proxy situation from chief complaint
export const PROXY_INDICATORS = [
  'my baby', 'my child', 'my son', 'my daughter', 'my kid',
  'my infant', 'my toddler', 'my newborn', 'my teenager',
  'my husband', 'my wife', 'my mother', 'my father', 'my mom', 'my dad',
  'my grandmother', 'my grandfather', 'my grandma', 'my grandpa',
  'my partner', 'my spouse', 'my parent',
];

// Fallback symptom questions - used if LLM hasn't returned questions yet
// These supplement the LLM questions, NOT replace prerequisites
export const FALLBACK_SYMPTOM_QUESTIONS = [
  "On a scale of 1 to 10, how severe would you say it is?",
  "Is there anything that makes it better or worse?",
  "Have you had this problem before?",
  "Have you taken any medications or treatments for this?",
];

// =============================================================================
// WORD NUMBERS (for parsing age from speech)
// =============================================================================

export const WORD_NUMBERS = {
  'zero': 0, 'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
  'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
  'eleven': 11, 'twelve': 12, 'thirteen': 13, 'fourteen': 14, 'fifteen': 15,
  'sixteen': 16, 'seventeen': 17, 'eighteen': 18, 'nineteen': 19, 'twenty': 20,
  'thirty': 30, 'forty': 40, 'fifty': 50, 'sixty': 60, 'seventy': 70,
  'eighty': 80, 'ninety': 90,
};

// =============================================================================
// FILLER WORDS (for fuzzy question matching)
// =============================================================================

export const FILLER_WORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
  'may', 'might', 'can', 'shall', 'must', 'i', 'you', 'your', 'we', 'our',
  'any', 'some', 'all', 'each', 'every', 'this', 'that', 'these', 'those',
  'what', 'which', 'who', 'whom', 'when', 'where', 'why', 'how',
  'of', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'about', 'from',
  'and', 'or', 'but', 'if', 'then', 'else', 'so', 'yet', 'both', 'either',
  'please', 'could', 'tell', 'me', 'describe', 'explain', 'noticed', 'experienced',
  'recently', 'lately', 'currently', 'now', 'ever', 'before', 'after',
  'sir', 'maam', "ma'am", 'madam',
  'feel', 'feeling', 'felt', 'like', 'just', 'also', 'well', 'get', 'got',
  'going', 'went', 'know', 'think', 'yes', 'yeah', 'not', 'its', "it's",
  'really', 'pretty', 'much', 'very', 'kind', 'sort', 'lot', 'bit',
  'started', 'start', 'since', 'still', 'thing', 'things', 'sometimes',
]);

// =============================================================================
// AAMC RECOGNIZED MEDICAL SPECIALTIES
// =============================================================================

/**
 * Medical specialties - imported from shared constants
 * Re-exported for backward compatibility with existing symptom checker code
 */
export { 
  AAMC_SPECIALTIES, 
  DEFAULT_SPECIALTY, 
  getSpecialtyLabel 
} from '@healthdesk/shared-services';

// =============================================================================
// INCOMPLETE SENTENCE PATTERNS
// =============================================================================

export const INCOMPLETE_PATTERNS = [
  // Sentence starters without completion
  /^i'?m having$/,
  /^i'?ve been$/,
  /^i'?ve got$/,
  /^it feels like$/,
  /^there'?s this$/,
  /^i feel$/,
  /^i have$/,
  /^my .+ is$/,
  /^my .+ are$/,
  /^i think i$/,
  /^i'?m experiencing$/,
  /^i started$/,
  /^it started$/,
  /^i noticed$/,
  
  // Ends with conjunctions or prepositions (clearly incomplete)
  /\b(and|but|or|because|since|when|if|that|which|where|while|so|then|like|with|for|to|in|on|at|from|about|into)$/,
  
  // Ends with articles (incomplete noun phrase)
  /\b(a|an|the)$/,
  
  // Ends with "is" or "are" (incomplete predicate)
  /\b(is|are|was|were|been|being)$/,
];
